import React, { useCallback, useEffect, useState } from "react";
import { api, apiJson, getToken } from "@/api/client.js";
import { useOrignalUi } from "@/hooks/useOrignalUi.js";

const TABS = [
  "resume",
  "messages",
  "depenses",
  "photos",
  "territoire",
  "nourriture",
  "taches",
  "sejour",
  "vehicules",
  "souvenirs",
  "ignores",
  "importes",
];

const PHOTO_CATS = ["groupe", "chalet", "territoire", "repas", "vehicule", "facture", "document_financier", "souvenir", "autre"];
const STATUSES = ["to_validate", "approved", "rejected", "imported"];

function fileUrl(item) {
  if (!item?.id || !item.file_relpath) return "";
  const token = encodeURIComponent(getToken());
  return `/api/admin/facebook/items/${item.id}/file?access_token=${token}`;
}

function StatusBadge({ status }) {
  const { ui } = useOrignalUi();
  return <span className={`og-badge og-fb-status og-fb-status--${status}`}>{ui.fbImport.status[status] || status}</span>;
}

export function FacebookImportPanel() {
  const { ui } = useOrignalUi();
  const t = ui.fbImport;
  const [tab, setTab] = useState("resume");
  const [sum, setSum] = useState(null);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [msg, setMsg] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [photoCat, setPhotoCat] = useState("autre");

  const reloadSummary = useCallback(async () => {
    setSum(await api("/api/admin/facebook/summary"));
  }, []);

  const reloadItems = useCallback(async (which) => {
    if (which === "resume") return;
    const data = await api(`/api/admin/facebook/items?tab=${which}`);
    setItems(data.items || []);
    setSelected(new Set());
  }, []);

  useEffect(() => {
    reloadSummary().catch(() => {});
  }, [reloadSummary]);

  useEffect(() => {
    reloadItems(tab).catch(() => {});
  }, [tab, reloadItems]);

  async function loadStaging() {
    setBusy(true);
    setMsg("");
    try {
      const res = await apiJson("/api/admin/facebook/load", {});
      setSum({ loaded: true, ...res });
      setMsg(t.loaded);
      await reloadItems(tab);
    } catch {
      setMsg(t.loadError);
    } finally {
      setBusy(false);
    }
  }

  async function patch(id, body) {
    await apiJson(`/api/admin/facebook/items/${id}`, body, "PATCH");
    await reloadItems(tab);
    await reloadSummary();
  }

  async function bulk(body) {
    const ids = [...selected];
    if (!ids.length) return;
    await api("/api/admin/facebook/items/bulk", { method: "POST", body: JSON.stringify({ ids, ...body }) });
    await reloadItems(tab);
    await reloadSummary();
  }

  async function doImport(confirm) {
    setBusy(true);
    try {
      const res = await apiJson("/api/admin/facebook/import", { confirm: !!confirm });
      setPreview(res);
      if (res.ok) {
        setMsg(t.imported);
        await reloadSummary();
        await reloadItems(tab);
      }
    } catch {
      setMsg(t.importError);
    } finally {
      setBusy(false);
    }
  }

  const counts = sum?.counts || {};

  return (
    <section className="og-fb" data-testid="facebook-import">
      <h2>{t.title}</h2>
      <p className="og-lead">{t.lead}</p>
      <div className="og-chips og-fb-tabs" role="tablist">
        {TABS.map((id) => (
          <button key={id} type="button" className={`og-chip${tab === id ? " is-active" : ""}`} onClick={() => setTab(id)} data-testid={`fb-tab-${id}`}>
            {t.tabs[id]}
          </button>
        ))}
      </div>

      {tab === "resume" ? (
        <div className="og-fb-resume" data-testid="fb-resume">
          <p><strong>{sum?.conversation || "Chasse Port-Neuf 2026"}</strong> · {sum?.period}</p>
          <div className="og-kpi og-fb-kpi">
            {[
              ["messages", 266],
              ["photos", 83],
              ["videos", 7],
              ["attachments", 4],
              ["expense_rows", 45],
              ["tache", 8],
              ["nourriture", 18],
              ["territoire", 22],
              ["vehicule", 11],
              ["sejour", 26],
              ["souvenir", 26],
            ].map(([k, expected]) => (
              <div key={k} className="og-stat"><strong>{expected}</strong><span>{t.kpis[k] || k}</span></div>
            ))}
          </div>
          <div className="og-kpi og-fb-kpi">
            {["to_validate", "approved", "rejected", "imported", "sensitive_excluded"].map((k) => (
              <div key={k} className="og-stat"><strong>{counts[k] ?? 0}</strong><span>{t.status[k] || k}</span></div>
            ))}
          </div>
          <p className="og-hint">{t.noMass}</p>
          <div className="og-fb-actions">
            <button type="button" className="og-btn og-btn--primary" onClick={loadStaging} disabled={busy} data-testid="fb-load">{t.load}</button>
            <button type="button" className="og-btn" onClick={() => doImport(false)} disabled={busy} data-testid="fb-preview">{t.previewImport}</button>
            <button type="button" className="og-btn og-btn--primary" onClick={() => doImport(true)} disabled={busy} data-testid="fb-import">{t.confirmImport}</button>
            <button type="button" className="og-btn" onClick={async () => { await apiJson("/api/admin/facebook/cancel", {}); await reloadSummary(); }} data-testid="fb-cancel">{t.cancelImport}</button>
          </div>
          {preview ? (
            <p className="og-hint" data-testid="fb-import-preview">
              {t.previewLine
                .replace("{m}", preview.messages)
                .replace("{p}", preview.photos)
                .replace("{e}", preview.expenses)
                .replace("{d}", preview.documents)
                .replace("{s}", preview.souvenirs)}
            </p>
          ) : null}
          <h3>{t.authors}</h3>
          <div className="og-fb-cards">
            {(sum?.authors || []).map((a) => (
              <article key={a.id} className="og-card" data-testid={`fb-author-${a.facebook_name}`}>
                <strong>{a.display_name}</strong>
                <small>{a.facebook_name}</small>
                <StatusBadge status={a.mapping_status === "ambiguous" ? "to_validate" : "approved"} />
                <span className="og-badge">{a.mapping_status}</span>
                {a.mapping_status === "ambiguous" ? <p className="og-crit">{t.ambiguous}</p> : null}
                {a.historical_only ? <p className="og-hint">{t.historical}</p> : null}
                <p>{a.notes}</p>
              </article>
            ))}
          </div>
          <h3>{t.knownExpenses}</h3>
          <ul>
            {(sum?.known_expenses || []).map((k) => (
              <li key={k.amount}>{k.amount} $ · {k.hint} · {k.found ? t.found : t.missing}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {tab !== "resume" ? (
        <div>
          {tab === "photos" ? (
            <div className="og-fb-bulk">
              <select className="og-input" value={photoCat} onChange={(e) => setPhotoCat(e.target.value)}>
                {PHOTO_CATS.map((c) => <option key={c} value={c}>{t.photoCats[c] || c}</option>)}
              </select>
              <button type="button" className="og-btn" onClick={() => bulk({ photo_category: photoCat, status: "approved" })} data-testid="fb-photo-bulk">{t.bulkApproveCat}</button>
              <button type="button" className="og-btn" onClick={() => bulk({ status: "rejected" })}>{t.bulkReject}</button>
            </div>
          ) : (
            <div className="og-fb-bulk">
              <button type="button" className="og-btn" onClick={() => bulk({ status: "approved" })} data-testid="fb-bulk-approve">{t.approve}</button>
              <button type="button" className="og-btn" onClick={() => bulk({ status: "rejected" })}>{t.reject}</button>
            </div>
          )}
          <div className={tab === "photos" ? "og-fb-photos" : "og-fb-cards"}>
            {items.map((it) => (
              <article key={it.id} className={`og-card og-fb-card${it.keep_candidate ? " og-card--amber" : ""}`} data-testid={`fb-item-${it.source_id}`}>
                <label className="og-fb-check">
                  <input type="checkbox" checked={selected.has(it.id)} onChange={(e) => {
                    const next = new Set(selected);
                    if (e.target.checked) next.add(it.id); else next.delete(it.id);
                    setSelected(next);
                  }} />
                  <StatusBadge status={it.status} />
                </label>
                <p><strong>{it.display_author}</strong>{it.author_ambiguous ? ` · ${t.ambiguous}` : ""}</p>
                <small>{it.occurred_at}</small>
                {it.item_type === "expense" ? (
                  <p>
                    <strong>{it.amount} $</strong> · {it.title}
                    {it.duplicate_of_id ? <span className="og-badge">{t.duplicate}</span> : null}
                    {it.keep_candidate ? <span className="og-badge">{t.keep}</span> : null}
                    {it.title?.includes("confirmer") ? <span className="og-crit">{t.confirmDesc}</span> : null}
                  </p>
                ) : null}
                {it.body ? <p className="og-fb-body">{it.body}</p> : null}
                {it.file_relpath && it.item_type === "photo" ? (
                  <img src={fileUrl(it)} alt="" className="og-fb-thumb" />
                ) : null}
                {it.item_type === "photo" ? (
                  <select className="og-input" value={it.photo_category || "autre"} onChange={(e) => patch(it.id, { photo_category: e.target.value })}>
                    {PHOTO_CATS.map((c) => <option key={c} value={c}>{t.photoCats[c] || c}</option>)}
                  </select>
                ) : null}
                {it.status !== "sensitive_excluded" && it.status !== "imported" ? (
                  <div className="og-fb-row">
                    {STATUSES.filter((s) => s !== "imported").map((s) => (
                      <button key={s} type="button" className="og-btn" onClick={() => patch(it.id, { status: s })}>{t.status[s]}</button>
                    ))}
                    {it.author_ambiguous ? (
                      <>
                        <button type="button" className="og-btn" onClick={() => patch(it.id, { display_author: "Chantal", member_id: "mem_chantal" })}>Chantal</button>
                        <button type="button" className="og-btn" onClick={() => patch(it.id, { display_author: "Luc", member_id: "mem_luc" })}>Luc</button>
                        <button type="button" className="og-btn" onClick={() => patch(it.id, { display_author: "Chantal / Luc", member_id: null })}>Chantal / Luc</button>
                      </>
                    ) : null}
                  </div>
                ) : null}
              </article>
            ))}
          </div>
        </div>
      ) : null}
      {msg ? <p className="og-hint" data-testid="fb-msg">{msg}</p> : null}
    </section>
  );
}
